import React, { useEffect, useRef, useState } from "react";
import { CalendarRange, Inbox, Loader2 } from "lucide-react";
import { D2C_OPS_ORDERS_API_URL } from "../../Shared/constant";
import { Badge } from "./ui/badge";
import { Card, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { NativeSelect } from "./ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, clickableRow } from "./ui/table";
import Pagination from "./Pagination";
import StateBlock, { EmptyState } from "./StateBlock";
import useD2cGet from "./useD2cGet";
import {
  ORDERS_MAX_DAYS,
  SUMMARY_MAX_DAYS,
  buildQuery,
  formatDateTime,
  formatHours,
  formatMinutes,
  formatRupees,
  humaniseCode,
  rangeDays,
} from "./d2cFormat";

// Same vocabulary as the summary's `outcomes` object.
export const OUTCOMES = [
  "delivered",
  "in_progress",
  "awaiting_acceptance",
  "nobody_accepted",
  "released",
  "cancelled",
  "handed_back",
];

const OUTCOME_TONE = {
  delivered: "success",
  in_progress: "info",
  awaiting_acceptance: "info",
  nobody_accepted: "warning",
  released: "warning",
  handed_back: "warning",
  cancelled: "danger",
};
const ERP_TONE = { complete: "success", in_progress: "info", not_started: "warning", failed: "danger" };

const PAGE_SIZES = [25, 50, 100];

const dealerLabel = (dealer) =>
  dealer ? [dealer.name, dealer.franchise_code].filter(Boolean).join(" · ") : "-";

const OrdersList = ({ from, to, storeId, regionId, city, refreshTick, onOpen }) => {
  const [outcome, setOutcome] = useState("");
  const [limit, setLimit] = useState(50);
  // Any filter change sends the list back to page 1 without a second request.
  const filterKey = [from, to, storeId, regionId, outcome, limit].join("|");
  const [paging, setPaging] = useState({ key: filterKey, page: 1 });
  const page = paging.key === filterKey ? paging.page : 1;
  const setPage = (next) => setPaging({ key: filterKey, page: next });

  // The OMS refuses a longer list with a 400; say why instead of asking.
  const tooLong = rangeDays(from, to) > ORDERS_MAX_DAYS;
  const url =
    from && to && !tooLong
      ? D2C_OPS_ORDERS_API_URL + buildQuery({ from, to, outcome, store_id: storeId, region_id: regionId, page, limit })
      : null;
  const { data, loading, error, reload } = useD2cGet(url);

  // The page's Refresh button reloads this list too.
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => {
    if (refreshTick) reloadRef.current();
  }, [refreshTick]);
  const items = data?.items || [];
  const total = Number(data?.total) || 0;

  return (
    <Card>
      <CardHeader className="tw-items-center">
        <div>
          <CardTitle className="tw-flex tw-items-center tw-gap-2">
            Orders <span className="tw-font-normal tw-text-muted-foreground">({total.toLocaleString("en-IN")})</span>
            {loading && items.length > 0 && <Loader2 className="tw-size-3.5 tw-animate-spin tw-text-muted-foreground" aria-hidden="true" />}
          </CardTitle>
          <CardDescription>
            This list covers up to {ORDERS_MAX_DAYS} days; the numbers above cover up to {SUMMARY_MAX_DAYS}.
            {city && " City filter applies to the numbers above, not this list."}
          </CardDescription>
        </div>
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          <NativeSelect
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            aria-label="Filter orders by outcome"
            className="tw-w-44"
          >
            <option value="">All outcomes</option>
            {OUTCOMES.map((value) => (
              <option key={value} value={value}>
                {humaniseCode(value)}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            aria-label="Orders per page"
            className="tw-w-36"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </NativeSelect>
        </div>
      </CardHeader>

      {tooLong ? (
        <EmptyState icon={CalendarRange}>
          The orders list covers at most {ORDERS_MAX_DAYS} days. Pick a shorter date range to see orders.
        </EmptyState>
      ) : (
        <StateBlock
          loading={loading}
          error={error}
          onRetry={reload}
          isEmpty={items.length === 0}
          emptyIcon={Inbox}
          emptyText="No D2C orders match these filters."
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Pincode / city</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Dealers offered</TableHead>
                <TableHead>Accepted by</TableHead>
                <TableHead className="tw-text-right">To accept</TableHead>
                <TableHead className="tw-text-right">To deliver</TableHead>
                <TableHead className="tw-text-right">Fee</TableHead>
                <TableHead>ERP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((order) => (
                <TableRow
                  key={order.order_id}
                  role="button"
                  aria-label={`Open order ${order.order_code || order.order_id}`}
                  tabIndex={0}
                  className={clickableRow}
                  onClick={() => onOpen(order.order_id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onOpen(order.order_id);
                    }
                  }}
                >
                  <TableCell className="tw-font-mono tw-text-xs tw-font-medium tw-text-primary">
                    {order.order_code || order.order_id}
                  </TableCell>
                  <TableCell className="tw-text-muted-foreground">{formatDateTime(order.created_at)}</TableCell>
                  <TableCell>
                    {order.pincode || "-"}
                    {order.city && <div className="tw-text-xs tw-text-muted-foreground">{order.city}</div>}
                  </TableCell>
                  <TableCell>
                    <Badge tone={OUTCOME_TONE[order.outcome] || "neutral"}>{humaniseCode(order.outcome)}</Badge>
                  </TableCell>
                  <TableCell title={order.reason || ""}>{humaniseCode(order.reason)}</TableCell>
                  <TableCell className="tw-whitespace-normal">
                    {order.dealers_offered ?? 0}
                    {order.offered_to?.length > 0 && (
                      <div className="tw-max-w-[220px] tw-truncate tw-text-xs tw-text-muted-foreground">
                        {order.offered_to.map((dealer) => dealer.name || dealer.franchise_code).join(", ")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{dealerLabel(order.accepted_by)}</TableCell>
                  <TableCell className="tw-text-right tw-tabular-nums">{formatMinutes(order.minutes_to_accept)}</TableCell>
                  <TableCell className="tw-text-right tw-tabular-nums">{formatHours(order.hours_to_deliver)}</TableCell>
                  <TableCell className="tw-text-right tw-tabular-nums">{formatRupees(order.fee_amount)}</TableCell>
                  <TableCell>
                    <Badge tone={ERP_TONE[order.erp_status] || "neutral"}>{humaniseCode(order.erp_status)}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination page={page} limit={limit} total={total} onPage={setPage} />
        </StateBlock>
      )}
    </Card>
  );
};

export default OrdersList;
